import type { Undefinable } from "@core/domain/types/undefinable.type";
import type { Farm, FarmResume } from "@farm/domain/models/farm.model";
import { FarmGetByIdUseCase } from "@farm/domain/usecases/farm-get-by-id.usecase";
import { BaseViewModel } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class FarmWebPageViewmodel extends BaseViewModel {
    @observable
    selectedFarm: Undefinable<Readonly<Farm>> = undefined;

    constructor(
        @inject(FarmGetByIdUseCase)
        private readonly farmGetByIdUseCase: FarmGetByIdUseCase,
    ) {
        super();
        makeObservable(this);
    }

    /**
     * Selects or deselects a farm.
     * If the already-active farm is clicked, it is deselected.
     */
    @action
    async selectFarm(
        farmResume: Undefinable<Readonly<FarmResume>>,
    ): Promise<void> {
        if (!farmResume) {
            runInAction(() => {
                this.selectedFarm = undefined;
            });
            return;
        }

        const id = farmResume.id;
        if (this.selectedFarm?.id === id) {
            runInAction(() => {
                this.selectedFarm = undefined;
            });
            return;
        }

        const farm = await this.farmGetByIdUseCase.execute(Number(id.value));
        runInAction(() => {
            this.selectedFarm = farm ?? undefined;
        });
    }

    /**
     * Refreshes the data of the currently selected farm.
     */
    @action
    async refreshSelectedFarm(): Promise<void> {
        if (!this.selectedFarm) {
            return;
        }

        const farm = await this.farmGetByIdUseCase.execute(
            Number(this.selectedFarm.id.value),
        );
        runInAction(() => {
            this.selectedFarm = farm ?? undefined;
        });
    }
}
